/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HtmlCssLinkNavPanel — maps links and buttons inside an html_css_workspace
 * section to Page Builder pages, without the admin editing the markup.
 *
 * The section's HTML is parsed on every keystroke-settled render, so every <a>,
 * <button> and role="button" shows up as a row with a target picker. Choosing a
 * page writes an entry into section_config.link_navigations keyed by a CSS
 * selector; arcee-client's HtmlCssSection re-matches that selector inside the
 * sandboxed iframe and performs the navigation in the parent React scope.
 *
 * Shared by HtmlCssWorkspaceModal (full-screen tab) and HtmlCssEditor (compact
 * sidebar tab), mirroring how HtmlCssDataBindingsPanel is shared.
 */

import React, { useMemo, useState } from 'react';
import { usePages } from '../../hooks/usePages';
import { mergeLinkNavigations, resolveNavHref, newNavId } from '../../utils/htmlLinkTargets';

const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 };
const inputStyle = {
  width: '100%',
  padding: '5px 8px',
  fontSize: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 5,
  outline: 'none',
  background: '#fff',
};

const TAG_ICON = {
  a: 'bi-link-45deg',
  button: 'bi-hand-index-thumb',
};

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      role="switch"
      aria-checked={!!value}
      style={{
        width: 34, height: 19, borderRadius: 10, cursor: 'pointer', flexShrink: 0,
        background: value ? 'var(--primary-color)' : '#e2e8f0',
        position: 'relative', transition: 'background 0.15s',
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 18 : 3,
        width: 13, height: 13, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function PageSelect({ value, pages, onSelect }) {
  const pub  = pages.filter((p) => (p.visibility || 'public') === 'public');
  const priv = pages.filter((p) => (p.visibility || 'public') === 'private');

  const handle = (e) => {
    const page = pages.find((p) => String(p.id) === e.target.value);
    onSelect(page || null);
  };

  const option = (p) => (
    <option key={p.id} value={String(p.id)}>
      {p.name}{p.status === 'draft' ? ' (draft)' : ''} — /{p.slug}
    </option>
  );

  return (
    <select style={inputStyle} value={value ? String(value) : ''} onChange={handle}>
      <option value="">— Select a page —</option>
      {pub.length > 0 && <optgroup label="Public pages">{pub.map(option)}</optgroup>}
      {priv.length > 0 && <optgroup label="Private pages">{priv.map(option)}</optgroup>}
    </select>
  );
}

function LinkRow({ row, pages, onChange, onRemove }) {
  const nav = row.nav;
  const navType = nav?.navType || 'none';
  const href = resolveNavHref(nav);

  const selectedPage = nav?.targetPageId
    ? pages.find((p) => String(p.id) === String(nav.targetPageId))
    : null;

  const patch = (changes) => onChange({ ...(nav || {}), ...changes });

  const setNavType = (value) => {
    if (value === 'none') { onRemove(); return; }
    patch({
      id: nav?.id || newNavId(),
      selector: row.selector,
      label: row.label,
      tag: row.tag,
      navType: value,
    });
  };

  const handlePage = (page) => {
    patch({
      id: nav?.id || newNavId(),
      selector: row.selector,
      label: row.label,
      tag: row.tag,
      navType: 'page',
      targetPageId: page ? String(page.id) : '',
      targetPageName: page?.name || '',
      targetPageSlug: page?.slug || '',
      targetPageType: page?.visibility || 'public',
    });
  };

  return (
    <div style={{
      padding: 10,
      marginBottom: 8,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: `3px solid ${navType === 'none' ? '#e2e8f0' : navType === 'page' ? '#16a34a' : '#ea580c'}`,
      borderRadius: 6,
    }}>
      {/* Element identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <i className={`bi ${TAG_ICON[row.tag] || 'bi-cursor'}`} style={{ fontSize: 13, color: '#7c3aed', flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label || <span style={{ color: '#94a3b8', fontWeight: 400 }}>(no text)</span>}
        </span>
        <span style={{ fontSize: 9, background: '#f1f5f9', color: '#64748b', borderRadius: 3, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>
          {`<${row.tag}>`}
        </span>
        {row.href && (
          <span title={`Existing href: ${row.href}`} style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
            href={row.href.length > 24 ? `${row.href.slice(0, 24)}…` : row.href}
          </span>
        )}
        {!row.detected && (
          <span
            title="No element in the current HTML matches this selector — the markup may have changed, or it is rendered by your JS at runtime."
            style={{ fontSize: 9, background: '#fef2f2', color: '#dc2626', borderRadius: 3, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}
          >
            NOT IN HTML
          </span>
        )}
        <code style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto', flexShrink: 0 }} title={row.selector}>
          {row.selector.length > 30 ? `…${row.selector.slice(-30)}` : row.selector}
        </code>
      </div>

      {/* Target configuration */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 0.9 }}>
          <div style={labelStyle}>On click</div>
          <select style={inputStyle} value={navType} onChange={(e) => setNavType(e.target.value)}>
            <option value="none">Do nothing</option>
            <option value="page">Go to page</option>
            <option value="url">Go to URL</option>
          </select>
        </div>

        {navType === 'page' && (
          <div style={{ flex: 1.6 }}>
            <div style={labelStyle}>Page</div>
            <PageSelect value={nav?.targetPageId} pages={pages} onSelect={handlePage} />
          </div>
        )}

        {navType === 'url' && (
          <div style={{ flex: 1.6 }}>
            <div style={labelStyle}>URL</div>
            <input
              type="text"
              style={inputStyle}
              value={nav?.url || ''}
              placeholder="https://example.com or /sign-up"
              onChange={(e) => patch({ url: e.target.value })}
            />
          </div>
        )}

        {navType !== 'none' && (
          <div style={{ flexShrink: 0, paddingBottom: 4 }}>
            <div style={labelStyle}>New tab</div>
            <Toggle value={!!nav?.openInNewTab} onChange={(v) => patch({ openInNewTab: v })} />
          </div>
        )}

        {!row.detected && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove this mapping"
            style={{
              flexShrink: 0, width: 26, height: 26, border: '1px solid #fecaca',
              borderRadius: 5, background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: 13,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Resolved destination + caveats */}
      {navType === 'page' && (
        <div style={{ marginTop: 7, fontSize: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {href ? (
            <span style={{ color: '#16a34a' }}>
              <i className="bi bi-arrow-right-short" />
              Navigates to <code style={{ color: '#16a34a' }}>{href}</code>
            </span>
          ) : (
            <span style={{ color: '#b45309' }}>
              <i className="bi bi-exclamation-triangle me-1" />
              Pick a page — nothing happens until one is selected
            </span>
          )}
          {selectedPage?.status === 'draft' && (
            <span style={{ background: '#fef9c3', color: '#854d0e', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>
              Draft page may 404 for visitors
            </span>
          )}
          {selectedPage?.visibility === 'private' && (
            <span style={{ background: '#f0f9ff', color: '#0369a1', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>
              Visitors will be asked to sign in
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function HtmlCssLinkNavPanel({ appId, html, navigations, onChange }) {
  const navs = useMemo(() => navigations || [], [navigations]);
  const [customSelector, setCustomSelector] = useState('');

  const { data: pagesData } = usePages(appId);
  const pages = pagesData?.pages || pagesData || [];

  const rows = useMemo(() => mergeLinkNavigations(html, navs), [html, navs]);

  const upsert = (nav) => {
    const exists = navs.some((n) => n.id === nav.id);
    onChange(exists ? navs.map((n) => (n.id === nav.id ? nav : n)) : [...navs, nav]);
  };

  const removeBySelector = (selector) => {
    onChange(navs.filter((n) => n.selector !== selector));
  };

  const addCustom = () => {
    const selector = customSelector.trim();
    if (!selector || navs.some((n) => n.selector === selector)) return;
    onChange([...navs, {
      id: newNavId(),
      selector,
      label: '',
      tag: 'a',
      navType: 'page',
      targetPageId: '',
      targetPageSlug: '',
    }]);
    setCustomSelector('');
  };

  const configuredCount = navs.filter((n) => resolveNavHref(n) !== null).length;

  return (
    <div style={{ padding: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10 }}>
        LINK NAVIGATION
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        Point any link or button in your HTML at a page you built in the Page Builder —
        no markup changes needed. Buttons and links are detected from the HTML tab;
        pick a destination and the click is handled by the app router.
      </div>

      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
          No links or buttons found in this section's HTML yet.
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
          {rows.length} clickable element{rows.length === 1 ? '' : 's'} · {configuredCount} linked
        </div>
      )}

      {rows.map((row) => (
        <LinkRow
          key={row.selector}
          row={row}
          pages={pages}
          onChange={upsert}
          onRemove={() => removeBySelector(row.selector)}
        />
      ))}

      {/* Escape hatch for markup the section's own JS renders at runtime, which
          is not present in the authored HTML and so cannot be auto-detected. */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
        <div style={labelStyle}>Add by CSS selector</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            style={{ ...inputStyle, flex: 1 }}
            value={customSelector}
            placeholder="#cta-button — for elements your JS renders at runtime"
            onChange={(e) => setCustomSelector(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customSelector.trim()}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              border: '1px dashed #cbd5e1', borderRadius: 6, background: 'transparent',
              color: '#475569', cursor: customSelector.trim() ? 'pointer' : 'default',
              opacity: customSelector.trim() ? 1 : 0.5, whiteSpace: 'nowrap',
            }}
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
}
